/// <amd-module name="VSS/Platform/Util/Serialization" />
const msAjaxDateRegEx = new RegExp('(^|[^\\\\])\\"\\\\/Date\\((-?[0-9]+)(?:[a-zA-Z]|(?:\\+|-)[0-9]{4})?\\)\\\\/\\"', "g");

declare global {
    interface Window {
        jsonIslandData: { [key: string]: any };
    }
}

/**
 * Handle the raw text of a JSON response which may contain MSJSON style dates and
 * deserialize (JSON.parse) the data in a way that restores Date objects rather than
 * strings.
 *
 * MSJSON style dates are in the form:
 *
 *     "lastModified": "\/Date(1496158224000)\/"
 *
 * This format unnecessarily (but intentionally) escapes the "/" character. So the parsed
 * JSON will have no trace of the escape character (\).
 *
 * @param text The raw JSON text
 */
export function deserializeVssJsonObject<T>(text: string): T | null {
    function replaceMsJsonDates(object: any, parentObject: any, parentObjectKey: string) {
        if (parentObject && typeof object.__msjson_date__ === "number") {
            parentObject[parentObjectKey] = new Date(object.__msjson_date__);
            return;
        }

        for (const key in object) {
            const value = object[key];
            if (value !== null && typeof value === "object") {
                replaceMsJsonDates(object[key], object, key);
            }
        }
    }

    let deserializedData: T | null = null;

    if (text) {
        // Replace MSJSON dates with an object that we can easily identify after JSON.parse.
        // This replaces the string value (like "\/Date(1496158224000)\/") with a JSON object that
        // has an "__msjson_date__" key.
        const replacedText = text.replace(msAjaxDateRegEx, '$1{"__msjson_date__":$2 }');

        // Do the actual JSON deserialization
        deserializedData = JSON.parse(replacedText);

        // Go through the parsed object and create actual Date objects for our replacements made above
        if (replacedText !== text) {
            replaceMsJsonDates(deserializedData, null, "");
        }
    }

    return deserializedData;
}

/**
 * Deserialize an object that was serialized (using VSS serialization settings) to a <script> block as text
 *
 * @param elementId Id of the element that contains the serialized text as raw content
 * @param setWindowObject If true, set a global variable on the window object using the elementId as the name
 * @param removeScript If true, the script element will be removed from the dom after being read
 */
export function readJsonScript<T>(elementId: string, setWindowObject?: boolean, removeScript?: boolean): T | null {
    // Read from already-processed jsonIslandData property
    if (window.jsonIslandData && window.jsonIslandData[elementId]) {
        return window.jsonIslandData[elementId];
    }

    const element = document.getElementById(elementId);
    let deserializedObject = null;

    if (element) {
        const text = element.textContent;

        if (text) {
            deserializedObject = deserializeVssJsonObject<T>(text);
        }

        if (removeScript && element.parentElement) {
            element.parentElement.removeChild(element);
        }
    }

    updateJsonScriptValue(elementId, deserializedObject, setWindowObject);

    return deserializedObject;
}

/**
 * Updates the value used for deserializing a given JSON script block. Note that this doesn't
 * modify the script content block itself, but the cached-result variable used by readJsonScript.
 *
 * @param elementId Id of the element that contains the serialized text as raw content
 * @param value The deserialized value to store which will be returned by the next call to readJsonScript with this elementId.
 * @param setWindowObject If true, set a global variable on the window object using the elementId as the name
 */
export function updateJsonScriptValue<T>(elementId: string, value: T, setWindowObject?: boolean) {
    if (!window.jsonIslandData) {
        window.jsonIslandData = {};
    }
    window.jsonIslandData[elementId] = value;

    if (setWindowObject) {
        (window as any)[elementId] = value;
    }
}
